import moment from 'moment';
import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

import useVehicleAvailability from '../../api/useVehicleAvailability';

const formatDay = (date) => moment(date, 'YYYY-MM-DD').format('ddd D MMM');

/**
 * État et gestes des véhicules du jour pour l'écran Affectations camion :
 * panneau, confirmation « Marquer indisponible », levée d'une indisponibilité.
 * Chaque modification recharge l'écran (onChanged) et le panneau (refreshKey).
 *
 * @param {string} params.tourDate - La date de l'écran (YYYY-MM-DD)
 * @param {Function} params.onChanged - Rechargement de l'écran
 */
export default function useVehiclesOfTheDay({ tourDate, onChanged }) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  // Camion à marquer indisponible : { key, licensePlate, assignedCount }
  const [markTarget, setMarkTarget] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { setUnavailabilities, removeUnavailability, isSaving } =
    useVehicleAvailability();

  const changed = useCallback(() => {
    setRefreshKey((key) => key + 1);
    onChanged?.();
  }, [onChanged]);

  /** (dates, reason) => Promise<boolean> pour la confirmation */
  const markUnavailable = async (dates, reason) => {
    if (!markTarget) return false;
    const result = await setUnavailabilities(markTarget.key, dates, reason);
    if (!result) return false;

    // Plusieurs jours : seuls les autres jours ne se voient pas à l'écran
    const days = result.dates || dates;
    if (days.length > 1) {
      toast.success(
        `${markTarget.key} indisponible du ${formatDay(days[0])} au ${formatDay(
          days[days.length - 1]
        )}`
      );
    }
    changed();
    return true;
  };

  /** Lève l'indisponibilité du camion à la date de l'écran */
  const makeAvailable = async (vehicle) => {
    const result = await removeUnavailability(vehicle.key, tourDate);
    if (!result) return false;
    changed();
    return true;
  };

  return {
    isPanelOpen,
    openPanel: () => setIsPanelOpen(true),
    closePanel: () => setIsPanelOpen(false),
    markTarget,
    openMarkUnavailable: setMarkTarget,
    closeMarkUnavailable: () => setMarkTarget(null),
    markUnavailable,
    makeAvailable,
    refreshKey,
    isSaving,
  };
}
